/* Step 11b · item 2 (N1, owner 31-Oct-2026 12:21 KSA) — ONE searchable engagement picker for every screen that picks an
   engagement (the top bar "Select engagement", ISQM → Access, Methodology → deviation report filter).
   - Type any part of the client name, engagement name, firm reference or FY (EN or AR text); the list filters as you type,
     on the client side: the list the screen already holds (GET /engagements), no request per key.
   - ↑ / ↓ move, Enter picks, Esc closes. With the box empty the user's recent engagements come first.
   - The rules of today stay: the list is the one the server sends (Recycle-Bin and archived engagements are never in it;
     a member sees only their engagements); a locked / Report-Final engagement shows its lock and label and can be opened.
   - 500+ engagements: the search text of each row is built once per list (useMemo), a key press only filters strings. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Lock, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useI18n } from "@/contexts/I18nContext";
import { engagementLabel, refBeside, lockLabels } from "@/lib/engagementLabel";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { cn } from "@/lib/utils";

const RECENT_MAX = 5;
const RECENT_KEY = (userKey) => `af_eng_recent:${userKey || "anon"}`;

// Arabic-Indic and Eastern Arabic-Indic digits → 0-9; Arabic marks and tatweel removed; alef / yaa / taa-marbuta forms folded,
// so "أحمد", "احمد" and "إحمد" match each other, and "٢٠٢٥" matches 2025.
const AR_MARKS = /[ؐ-ًؚ-ٰٟۖ-ۭـ]/g;
export function normalizeSearch(s) {
  return String(s || "")
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(AR_MARKS, "")
    .replace(/[آأإٱ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** The text a row is found by: its label as shown, the client name, the engagement name, the firm reference and the FY. */
export function engagementSearchText(e, t) {
  const fy = e && e.financial_year ? String(e.financial_year) : "";
  return normalizeSearch([
    engagementLabel(e, t), refBeside(e), e && e.client_name, e && e.engagement_name, e && e.firm_engagement_ref,
    fy, fy && `FY${fy}`, fy && `FY ${fy}`,
  ].filter(Boolean).join(" | "));
}

/** Every word typed must appear somewhere in the row's text (any part, any order). */
export function matchesQuery(text, query) {
  const words = normalizeSearch(query).split(" ").filter(Boolean);
  return words.every((w) => text.includes(w));
}

export function readRecent(userKey) {
  try {
    const v = JSON.parse(window.localStorage.getItem(RECENT_KEY(userKey)) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch { return []; }
}

export function rememberRecent(userKey, id) {
  if (!id) return;
  try {
    const next = [id, ...readRecent(userKey).filter((x) => x !== id)].slice(0, RECENT_MAX);
    window.localStorage.setItem(RECENT_KEY(userKey), JSON.stringify(next));
  } catch { /* storage blocked: the list still works, without recents */ }
}

/** Remembers the engagement the user is in (however it was chosen: this picker, Engagements → Open, a dashboard link). */
export function useRememberRecentEngagement(userKey, currentId) {
  useEffect(() => { if (currentId) rememberRecent(userKey, currentId); }, [userKey, currentId]);
}

function OptionBody({ e, t }) {
  const locks = lockLabels(e, t, fmtDate);
  return (
    <>
      {locks.length > 0 && <Lock className="me-1 inline h-3 w-3 shrink-0 text-amber-600" data-testid={`engagement-lock-${e.id}`} />}
      <span className="min-w-0 break-words">
        {engagementLabel(e, t)}{refBeside(e) ? ` · ${refBeside(e)}` : ""}
        {locks.length > 0 && <span className="ms-1 text-[10px] text-amber-700" data-testid={`engagement-lock-label-${e.id}`}>· {locks.join(" · ")}</span>}
      </span>
    </>
  );
}

/**
 * props:
 *   engagements   the list the screen holds (rows of GET /engagements, or {id, client_name, …} rows)
 *   value         the chosen id ("" = none)
 *   onChange(id)  called with the picked id ("" for the "all" row)
 *   placeholder   the trigger text with nothing chosen
 *   allLabel      optional first row that picks "" (e.g. "All engagements" on a filter)
 *   userKey       whose recent engagements to show first (the signed-in user's id); omit for no recents
 *   testId        the trigger's data-testid (default "engagement-selector", as the top bar had)
 *   optionTestId  prefix of each row's data-testid (default "engagement-option")
 */
export default function EngagementPicker({
  engagements, value, onChange, placeholder, allLabel, userKey,
  testId = "engagement-selector", optionTestId = "engagement-option",
  className, triggerClassName, contentClassName, disabled,
}) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState([]);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const list = useMemo(() => asArray(engagements).filter((e) => e && e.id), [engagements]);

  // the search text of each row, built once per list / language
  const texts = useMemo(() => { const m = new Map(); for (const e of list) m.set(e.id, engagementSearchText(e, t)); return m; },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, lang]);

  useEffect(() => { if (open) { setRecent(userKey ? readRecent(userKey) : []); setQuery(""); } }, [open, userKey]);

  const q = query.trim();
  const { rows, recentCount } = useMemo(() => {
    const byId = new Map(list.map((e) => [e.id, e]));
    const rec = q ? [] : recent.map((id) => byId.get(id)).filter(Boolean);
    const recIds = new Set(rec.map((e) => e.id));
    const rest = (q ? list.filter((e) => matchesQuery(texts.get(e.id) || "", q)) : list).filter((e) => !recIds.has(e.id));
    const out = [];
    if (allLabel && !q) out.push({ all: true, id: "" });
    for (const e of rec) out.push({ e, id: e.id, recent: true });
    for (const e of rest) out.push({ e, id: e.id });
    return { rows: out, recentCount: rec.length };
  }, [list, texts, q, recent, allLabel]);

  // on opening, the chosen engagement is the active row; on typing, the first match is
  useEffect(() => {
    if (!open) return;
    if (q) { setActive(0); return; }
    const i = rows.findIndex((r) => r.id === (value || ""));
    setActive(i >= 0 ? i : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row-index="${active}"]`);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const pick = useCallback((row) => {
    if (!row) return;
    setOpen(false);
    if (row.id !== (value || "")) onChange(row.id);
  }, [onChange, value]);

  const onKeyDown = (ev) => {
    if (ev.key === "ArrowDown") { ev.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)); }
    else if (ev.key === "ArrowUp") { ev.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (ev.key === "Home") { ev.preventDefault(); setActive(0); }
    else if (ev.key === "End") { ev.preventDefault(); setActive(Math.max(rows.length - 1, 0)); }
    else if (ev.key === "Enter") { ev.preventDefault(); pick(rows[active]); }
    else if (ev.key === "Escape") { ev.preventDefault(); setOpen(false); }
  };

  const current = list.find((e) => e.id === value) || null;
  const title = current ? `${engagementLabel(current, t)}${refBeside(current) ? ` · ${refBeside(current)}` : ""}` : undefined;
  const listId = `${testId}-listbox`;
  const shown = rows.filter((r) => !r.all).length;

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={testId} title={title}
                  role="combobox" aria-expanded={open} aria-controls={listId} aria-haspopup="listbox"
                  className={cn("flex h-9 w-full items-center justify-between gap-1 rounded-md border border-input bg-transparent px-3 py-2 text-start text-sm shadow-sm",
                    "focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50", triggerClassName)}>
            <span className={cn("min-w-0 truncate", !current && !(allLabel && !value) && "text-muted-foreground")}>
              {current ? <>{lockLabels(current, t, fmtDate).length > 0 && <Lock className="me-1 inline h-3 w-3 text-amber-600" />}{title}</>
                : (allLabel && !value ? allLabel : placeholder)}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn("w-[min(28rem,calc(100vw-2rem))] p-0", contentClassName)}
                        onOpenAutoFocus={(ev) => { ev.preventDefault(); if (inputRef.current) inputRef.current.focus(); }}
                        data-testid={`${testId}-panel`}>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input ref={inputRef} value={query} onChange={(ev) => setQuery(ev.target.value)} onKeyDown={onKeyDown}
                   placeholder={t("s11bPick.search")} aria-label={t("s11bPick.search")} aria-controls={listId}
                   aria-activedescendant={rows[active] ? `${listId}-${active}` : undefined} aria-autocomplete="list"
                   className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                   data-testid={`${testId}-search`} autoComplete="off" spellCheck={false} />
          </div>
          <div ref={listRef} id={listId} role="listbox" className="max-h-[min(60vh,360px)] overflow-y-auto py-1"
               data-testid={`${testId}-list`}>
            {rows.map((r, i) => {
              const prev = rows[i - 1];
              const heading = q || recentCount === 0 ? null
                : (r.recent && !(prev && prev.recent)) ? "recent"
                : (!r.recent && !r.all && prev && prev.recent) ? "all" : null;
              const sel = r.id === (value || "");
              return (
                <React.Fragment key={r.all ? "__all" : r.id}>
                  {heading && (
                    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                         data-testid={`${testId}-heading-${heading}`}>{t(heading === "recent" ? "s11bPick.recent" : "s11bPick.all")}</div>
                  )}
                  <div id={`${listId}-${i}`} role="option" aria-selected={sel} data-row-index={i}
                       data-testid={r.all ? `${optionTestId}-all` : `${optionTestId}-${r.id}`}
                       data-active={i === active ? "true" : undefined} data-recent={r.recent ? "true" : undefined}
                       onMouseDown={(ev) => ev.preventDefault()} onMouseMove={() => { if (i !== active) setActive(i); }}
                       onClick={() => pick(r)}
                       className={cn("mx-1 flex cursor-pointer items-start gap-1 rounded px-2 py-1.5 text-sm",
                         i === active ? "bg-accent text-accent-foreground" : "hover:bg-accent/60")}>
                    <Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", sel ? "opacity-100" : "opacity-0")} />
                    {r.all ? <span>{allLabel}</span> : <OptionBody e={r.e} t={t} />}
                  </div>
                </React.Fragment>
              );
            })}
            {shown === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
                {q ? t("s11bPick.none").replace("{q}", q) : t("s11bPick.noEngagements")}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t px-3 py-1.5 text-[10px] text-muted-foreground">
            <span data-testid={`${testId}-count`}>{t("s11bPick.count").replace("{n}", String(shown)).replace("{total}", String(list.length))}</span>
            <span className="phone-hide">{t("s11bPick.keys")}</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
